import type { PMReport } from "../types/pmTypes";
import { PMStatus } from "../types/pmTypes";
import { calculatePMStatus } from "./pmUtils";

// ----- Service Queue Filtering (Req 7.4) -----

/**
 * Represents a service queue entry with searchable text fields.
 */
export interface ServiceQueueEntry {
    equipmentName: string;
    category: string;
    location: string;
    serviceCenter?: string;
    serviceLevel?: string;
    status?: string;
    [key: string]: unknown;
}

export interface ServiceQueueFilterCriteria {
    category?: string;
    serviceLevel?: string;
    status?: string;
    serviceCenter?: string;
}

const isAllFilter = (value?: string) => !value || value === 'all';

const sortCategoryNames = (values: Iterable<string>) =>
    [...new Set([...values].map((name) => name.trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );

/**
 * Queue category menu: names with Due Soon or Overdue equipment first,
 * then every other category that has a PM matrix.
 */
export function splitPmCategoryNames(dueOrOverdue: string[], withMatrix: string[]): { due: string[]; rest: string[] } {
    const due = sortCategoryNames(dueOrOverdue);
    const dueSet = new Set(due);
    const rest = sortCategoryNames(withMatrix).filter((name) => !dueSet.has(name));
    return { due, rest };
}

export interface PmQueueView {
    category: string;
    serviceCenter: string;
    serviceLevel: string;
    status: string;
}

const EMPTY_QUEUE_VIEW: PmQueueView = {
    category: 'all',
    serviceCenter: 'all',
    serviceLevel: 'all',
    status: 'all',
};

export function pmQueueViewStorageKey(companyId: string): string {
    return `pm-service-queue-view:${companyId}`;
}

/** Last queue filters for this company. A missing or broken save opens on the full list. */
export function readPmQueueView(storage: Pick<Storage, 'getItem'>, companyId: string): PmQueueView {
    if (!companyId) return EMPTY_QUEUE_VIEW;
    try {
        const raw = storage.getItem(pmQueueViewStorageKey(companyId));
        if (!raw) return EMPTY_QUEUE_VIEW;
        const parsed = JSON.parse(raw) as Partial<PmQueueView>;
        return {
            category: parsed.category?.trim() || 'all',
            serviceCenter: parsed.serviceCenter?.trim() || 'all',
            serviceLevel: parsed.serviceLevel?.trim() || 'all',
            status: parsed.status?.trim() || 'all',
        };
    } catch {
        return EMPTY_QUEUE_VIEW;
    }
}

export function writePmQueueView(storage: Pick<Storage, 'setItem'>, companyId: string, view: PmQueueView): void {
    if (!companyId) return;
    storage.setItem(pmQueueViewStorageKey(companyId), JSON.stringify(view));
}

/**
 * Filters service queue entries by a case-insensitive text search
 * across equipment name, category, and location.
 *
 * Validates: Requirement 7.4
 */
export function filterServiceQueue<T extends ServiceQueueEntry>(
    entries: T[],
    searchQuery: string,
): T[] {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return entries;

    return entries.filter((entry) => {
        const name = (entry.equipmentName ?? "").toLowerCase();
        const category = (entry.category ?? "").toLowerCase();
        const location = (entry.location ?? "").toLowerCase();
        return name.includes(query) || category.includes(query) || location.includes(query);
    });
}

/**
 * Filters service queue rows by category, service center, service level, and status dropdowns.
 * Unspecified or "all" criteria are ignored.
 */
export function applyServiceQueueFilters<T extends ServiceQueueEntry>(
    entries: T[],
    criteria: ServiceQueueFilterCriteria,
): T[] {
    return entries.filter((entry) => {
        if (!isAllFilter(criteria.category) && entry.category !== criteria.category) return false;
        if (!isAllFilter(criteria.serviceCenter) && entry.serviceCenter !== criteria.serviceCenter) return false;
        if (!isAllFilter(criteria.serviceLevel) && entry.serviceLevel !== criteria.serviceLevel) return false;
        if (!isAllFilter(criteria.status) && entry.status !== criteria.status) return false;
        return true;
    });
}

// ----- Report Filtering (Req 10.3) -----

export interface ReportFilterCriteria {
    dateRange?: { start?: string; end?: string };
    equipmentId?: string;
    serviceLevelId?: string;
    locationId?: string;
}

/**
 * Filters PM reports by optional criteria: date range, equipment, service level, location.
 * All specified criteria must match (AND logic). Unspecified criteria are ignored.
 *
 * For locationId filtering, the report must carry a locationId field (extended via generic or cast).
 *
 * Validates: Requirement 10.3
 */
export function filterReports<T extends PMReport & { locationId?: string | null }>(
    reports: T[],
    criteria: ReportFilterCriteria,
): T[] {
    return reports.filter((report) => {
        // Date range filter
        if (criteria.dateRange) {
            const serviceDate = report.serviceDate;
            if (criteria.dateRange.start && serviceDate < criteria.dateRange.start) return false;
            if (criteria.dateRange.end && serviceDate > criteria.dateRange.end) return false;
        }

        // Equipment filter
        if (criteria.equipmentId && report.unitId !== criteria.equipmentId) return false;

        // Service level filter
        if (criteria.serviceLevelId && report.serviceLevelId !== criteria.serviceLevelId) return false;

        // Location filter
        if (criteria.locationId && report.locationId !== criteria.locationId) return false;

        return true;
    });
}

// ----- Vendor Filtering (Req 12.5, 12.8) -----

/**
 * Minimal vendor interface for filtering and partitioning.
 */
export interface Vendor {
    name?: string | null;
    address?: string | null;
    phone?: string | null;
    fax?: string | null;
    repName?: string | null;
    archived?: boolean | null;
    [key: string]: unknown;
}

/**
 * Filters vendors by a case-insensitive text search across
 * name, address, phone, fax, and rep name fields.
 *
 * Validates: Requirement 12.5
 */
export function filterVendors<T extends Vendor>(vendors: T[], searchQuery: string): T[] {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return vendors;

    return vendors.filter((vendor) => {
        const fields = [
            vendor.name,
            vendor.address,
            vendor.phone,
            vendor.fax,
            vendor.repName,
        ];
        return fields.some((field) => (field ?? "").toLowerCase().includes(query));
    });
}

/**
 * Partitions vendors into active and archived lists.
 * Active = archived is falsy; Archived = archived is true.
 *
 * Validates: Requirement 12.8
 */
export function partitionVendorsByArchiveStatus<T extends Vendor>(
    vendors: T[],
): { active: T[]; archived: T[] } {
    const active: T[] = [];
    const archived: T[] = [];

    for (const vendor of vendors) {
        if (vendor.archived) {
            archived.push(vendor);
        } else {
            active.push(vendor);
        }
    }

    return { active, archived };
}

// ----- Status Counts (Req 7.1) -----

/**
 * Represents an enrolled unit with the fields needed for status computation.
 */
export interface EnrolledUnitForStatus {
    currentUM: number;
    targetUM: number;
    dueSoonPercentage: number;
    intervalUM?: number | null;
}

export interface StatusCounts {
    overdue: number;
    dueSoon: number;
    scheduled: number;
}

/**
 * Computes dashboard summary card counts by calculating each unit's PM status
 * and tallying Overdue, Due Soon, and Scheduled/OK.
 *
 * Validates: Requirement 7.1
 */
export function computeStatusCounts(units: EnrolledUnitForStatus[]): StatusCounts {
    const counts: StatusCounts = { overdue: 0, dueSoon: 0, scheduled: 0 };

    for (const unit of units) {
        const status = calculatePMStatus({
            currentUM: unit.currentUM,
            targetUM: unit.targetUM,
            dueSoonPercentage: unit.dueSoonPercentage,
            intervalUM: unit.intervalUM,
        });

        switch (status) {
            case PMStatus.OVERDUE:
                counts.overdue++;
                break;
            case PMStatus.DUE_SOON:
                counts.dueSoon++;
                break;
            case PMStatus.SCHEDULED:
                counts.scheduled++;
                break;
        }
    }

    return counts;
}
